import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../../app/itemGridPreferencesSlice";
import GridSectionLoader from "../../../components/CustomComponents/preLoad/gridSectionLoader";
import ColumnSelection from "../../../components/CustomComponents/ColumnSelection/ColumnSelection";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import swal from "sweetalert";
import CustomerGroupModel from "./customerGroupModel";
import showErrorToast, {
  showSuccessToast,
} from "../../../components/CustomComponents/toast/toast";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../../utils/preferencesUtils";
import ErrorBoundary from "../../../components/CustomComponents/ErrorBoundary/ErrorBoundary.jsx";
import AddButton from "../../../components/CustomComponents/Buttons/AddButton/AddButton";
import ModernGridToolbar from "../../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import useResetPageOnFilterChange from "../../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../../hooks/useInvalidateFetchOnFilterChange";
import {
  applyCatalogTotalFromFetch,
  applyCatalogTotalFromMutation,
} from "../../../utils/catalogTotalRecords";


import SortableColumnHeader from "../../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";

const CUSTOMER_GROUP_SORT_FIELDS = {
  name: "Name",
};

const CUSTOMER_GROUP_SORT_LABELS = {
  [CUSTOMER_GROUP_SORT_FIELDS.name]: "Name",
};

const ManageCustomersGroup = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "customerGroups";
  const defaultVisibleColumns = {
    description: true,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "ID", label: "ID" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const [customerGroups, setCustomerGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const groupsFetchSeqRef = useRef(0);

  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const SHOW_BULK_ACTIONS = false;

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: CUSTOMER_GROUP_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const fetchCustomerGroups = useCallback(async () => {
    const fetchSeq = ++groupsFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CUSTOMERS_GROUP, {
        params: {
          CompID,
          Search: search?.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
        },
      });

      if (fetchSeq !== groupsFetchSeqRef.current) return;

      const groupData = response.data.data || [];
      const groups = Array.isArray(groupData) ? groupData : [];
      setCustomerGroups(groups);

      if (!search.trim()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, groups.length);
      }
    } catch (error) {
      if (fetchSeq !== groupsFetchSeqRef.current) return;
      setCustomerGroups([]);
      showErrorToast("Failed to fetch customer groups.");
    } finally {
      if (fetchSeq === groupsFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, search, sortBy, sortOrder]);

  const totalRecords = customerGroups.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedCustomerGroups = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return customerGroups.slice(start, start + size);
  }, [customerGroups, pageNumber, pageSize]);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (
      needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)
    ) {
      const mergedColumns = mergeVisibleColumns(
        preferences.visibleColumns,
        defaultVisibleColumns
      );
      dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(groupsFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchCustomerGroups(), [fetchCustomerGroups]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const handleGroupSubmit = async (groupData, isUpdate) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        APIs.POST_ADD_UPDATE_CUSTOMER_GROUP,
        groupData
      );
      if (response.data.status) {
        const updatedGroup = response.data.data;

        if (isUpdate) {
          setCustomerGroups((prev) =>
            prev.map((group) =>
              group.id === updatedGroup.id ? updatedGroup : group
            )
          );
        } else {
          setCustomerGroups((prev) => {
            const next = [...prev, updatedGroup];
            if (!search.trim()) {
              applyCatalogTotalFromMutation(setCatalogTotalRecords, next.length);
            }
            return next;
          });
        }

        showSuccessToast(
          `Customer Type Group is ${isUpdate ? "Updated" : "Added"}.`
        );
      } else {
        showErrorToast(response.data.message || "Operation failed.");
      }
    } catch (error) {
      showErrorToast("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddGroupClick = () => {
    setGroupToEdit(null);
    setShowAddGroupModal(true);
  };

  const handleEditGroupClick = (group) => {
    setGroupToEdit(group);
    setShowAddGroupModal(true);
  };

  const DeleteCustomerTypeGroup = async (customerGroup) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${customerGroup.name}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.delete(APIs.DELETE_CUSTOMER_GROUP_TYPE, {
        params: {
          CompID,
          ID: customerGroup.id,
        },
      });

      if (response.data.status) {
        setCustomerGroups((prevGroups) => {
          const next = prevGroups.filter((delGroup) => delGroup.id !== customerGroup.id);
          if (!search.trim()) {
            applyCatalogTotalFromMutation(setCatalogTotalRecords, next.length);
          }
          return next;
        });
        showSuccessToast("Customer Type Group is Deleted.");
      } else {
        showErrorToast(response.data.message);
      }
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong.");
      setLoading(false);
    }
  };

  const handleSelectItem = (groupId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (pagedCustomerGroups[i]?.id) {
            newSelected.add(pagedCustomerGroups[i].id);
          }
        }
      } else {
        if (newSelected.has(groupId)) {
          newSelected.delete(groupId);
        } else {
          newSelected.add(groupId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(pagedCustomerGroups.map((item) => item.id));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No customer groups selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected customer groups?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((groupId) =>
          axiosInstance.delete(APIs.DELETE_CUSTOMER_GROUP_TYPE, {
            params: {
              CompID,
              ID: groupId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchCustomerGroups();
      showSuccessToast(
        `${selectedItems.size} customer groups deleted successfully.`
      );
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected customer groups.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No customer groups selected for editing.");
      return;
    }

    const selectedGroups = customerGroups.filter((group) =>
      selectedItems.has(group.id)
    );
    setGroupToEdit(selectedGroups);
    setShowAddGroupModal(true);
  };

  const truncateDescription = (description) => {
    if (!description) return "";
    return description.length > 50
      ? `${description.substring(0, 50)}...`
      : description;
  };

  const hasFiltersApplied = () => search.trim() !== "";
  const isUnfilteredView = !hasFiltersApplied();

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    isUnfilteredView &&
    catalogTotalRecords === 0;

  const showGridToolbar =
    initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No customer groups match your search "${search.trim()}". Try different keywords or clear filters to see all groups.`;
    }
    return "There are no customer groups to show right now. Add a new group or check back later.";
  };

  const renderNoDataMessage = () => {
    if (loading || customerGroups.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No customer groups found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <ErrorBoundary>
      <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Customer Group</li>
          </ol>
          <h1 className="page-header mb-0">Manage Customer Group</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={handleAddGroupClick}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white" />
            Add Customer Group
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && (
          <GridSectionLoader loading minHeight="400px" />
        )}

        {showEmptyCatalogState && (
          <AddButton onClick={handleAddGroupClick} title="Add Customer Group" />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Customer Groups"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              {SHOW_BULK_ACTIONS && selectedItems.size > 0 && (
                <div className="mb-3">
                  <div
                    className="p-3 bg-light rounded d-flex align-items-center justify-content-between"
                    style={{ border: "1px solid #dee2e6" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} group{selectedItems.size > 1 ? "s" : ""}{" "}
                        selected
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleBulkEdit}
                        title="Edit Selected Groups"
                      >
                        <i className="fa fa-edit me-1" />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={handleBulkDelete}
                        title="Delete Selected Groups"
                      >
                        <i className="fa fa-trash me-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && pagedCustomerGroups.length > 0 && (
                  <div
                    className="table-responsive table-container mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{
                        "--modern-grid-sort-active": themeColor,
                      }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {SHOW_BULK_ACTIONS && (
                            <th className="pt-2 pb-2">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  pagedCustomerGroups.length > 0 &&
                                  selectedItems.size === pagedCustomerGroups.length
                                }
                              />
                            </th>
                          )}
                          <SortableColumnHeader
                            sortField={CUSTOMER_GROUP_SORT_FIELDS.name}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleColumnSort}
                          >
                            Name
                          </SortableColumnHeader>
                          {visibleColumns.description && (
                            <th className="pt-2 pb-2">Description</th>
                          )}
                          <th className="pt-2 pb-2 text-start">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedCustomerGroups.map((group, index) => (
                          <tr key={group.id}>
                            {SHOW_BULK_ACTIONS && (
                              <td className="align-middle">
                                <input
                                  type="checkbox"
                                  checked={selectedItems.has(group.id)}
                                  onChange={(e) =>
                                    handleSelectItem(group.id, index, e)
                                  }
                                  data-chkbox-shiftsel="type1"
                                />
                              </td>
                            )}
                            <td
                              className="align-middle"
                              onClick={() => handleEditGroupClick(group)}
                              style={{ cursor: "pointer" }}
                            >
                              {group.name}
                            </td>
                            {visibleColumns.description && (
                              <td
                                className="align-middle"
                                onClick={() => handleEditGroupClick(group)}
                                style={{ cursor: "pointer" }}
                              >
                                {truncateDescription(group.description)}
                              </td>
                            )}
                            <td className="align-middle text-start">
                              <TableActionsMenu menuStyle={{ zIndex: 9999 }}>
                                <TableActionsItem
                                  icon="fa fa-edit"
                                  onClick={() => handleEditGroupClick(group)}
                                >
                                  Edit
                                </TableActionsItem>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => DeleteCustomerTypeGroup(group)}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <CustomerGroupModel
        isModalOpen={showAddGroupModal}
        setIsModalOpen={setShowAddGroupModal}
        onGroupSubmit={handleGroupSubmit}
        compID={CompID}
        groupToEdit={groupToEdit}
      />
      </div>
    </ErrorBoundary>
  );
};

export default ManageCustomersGroup;
